import { PayloadAction, createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { Collection, LoadingState, Resource, User } from '../lib/types';
import { RootState } from './store';
import {
  addResourceToCollection,
  createNewCollection,
  deleteCollection,
  fetchCsrfToken,
} from '../lib/api';

interface AddSelectedToCollectionParams {
  selectedCollectionId: number;
  removeSelected: boolean;
}

interface AddSelectedItemToCollectionParams {
  selectedCollectionId: number;
  resourceId: number;
}

interface AddSelectedToCollectionCompleteParams {
  resourceId: number;
  removeSelected: boolean;
}

export interface AsyncRequestProgress {
  loadingState: LoadingState;
  progress: number;
  result?: unknown;
}

export interface AppState {
  csrfToken: string;
  currentResource: Resource | null;
  selectedResources: Resource[];
  user: User;
  userCollections: Collection[];
  userCollectionLoading: boolean;
  userCollectionStale: boolean;
  addCollectionResourcesLoading: AsyncRequestProgress;
  createCollectionLoading: AsyncRequestProgress;
  removeCollectionLoading: AsyncRequestProgress;
}

export const getCsrfToken = createAsyncThunk('app/getCsrfToken', async () => {
  return await fetchCsrfToken();
});

export const addSelectedToCollection = createAsyncThunk(
  'app/addCollectionResources',
  async (
    { selectedCollectionId, removeSelected }: AddSelectedToCollectionParams,
    thunkAPI
  ) => {
    const state: RootState = thunkAPI.getState() as RootState;
    const selectedResources: Resource[] = appSelectors.selectedResources(state);
    return await Promise.allSettled(
      selectedResources.map(
        (resource): Promise<AddSelectedToCollectionCompleteParams> => {
          return addResourceToCollection(
            selectedCollectionId,
            resource.ref
          ).then((response): AddSelectedToCollectionCompleteParams => {
            if (response) {
              //increment progress counter
              thunkAPI.dispatch(
                appSlice.actions.incrementCollectionResourcesLoading()
              );
              return {
                resourceId: resource.ref,
                removeSelected,
              } as AddSelectedToCollectionCompleteParams;
            } else {
              throw `Could not add resource ${resource.ref} to collection`;
            }
          });
        }
      )
    );
  }
);

export const addSelectedItemToCollection = createAsyncThunk(
  'app/addCollectionResource',
  async (
    { selectedCollectionId, resourceId }: AddSelectedItemToCollectionParams,
    thunkAPI
  ) => {
    const state: RootState = thunkAPI.getState() as RootState;
    const currentResource: Resource | null =
      appSelectors.currentResource(state);

    if (!currentResource || currentResource.ref !== resourceId) {
      throw `Could not add resource ${resourceId} to collection`;
    }
    const selectedResources: Resource[] = [currentResource];
    return await Promise.allSettled(
      selectedResources.map(
        (resource): Promise<AddSelectedToCollectionCompleteParams> => {
          return addResourceToCollection(
            selectedCollectionId,
            resource.ref
          ).then((response): AddSelectedToCollectionCompleteParams => {
            if (response) {
              //increment progress counter
              thunkAPI.dispatch(
                appSlice.actions.incrementCollectionResourcesLoading()
              );
              return {
                resourceId: resource.ref,
              } as AddSelectedToCollectionCompleteParams;
            } else {
              throw `Could not add resource ${resource.ref} to collection`;
            }
          });
        }
      )
    );
  }
);

export const createCollection = createAsyncThunk(
  'app/createCollection',
  async (collectionName: string, { dispatch }) => {
    dispatch(appSlice.actions.clearCreateCollectionLoading());
    const collectionId: number = await createNewCollection(collectionName);
    dispatch(appSlice.actions.setUserCollectionStale());
    return collectionId;
  }
);

export const removeCollection = createAsyncThunk(
  'app/removeCollection',
  async (collectionId: number, { dispatch }) => {
    console.log('HERE');
    dispatch(appSlice.actions.clearRemoveCollectionLoading());
    await deleteCollection(collectionId);
    dispatch(appSlice.actions.setUserCollectionStale());
    return;
  }
);

const initialState: AppState = {
  csrfToken: '',
  currentResource: null,
  selectedResources: [],
  user: {
    username: '',
    loggedIn: false,
  },
  userCollections: [],
  userCollectionLoading: false,
  userCollectionStale: false,
  addCollectionResourcesLoading: {
    loadingState: 'idle',
    progress: 0,
  },
  createCollectionLoading: {
    loadingState: 'idle',
    progress: 0,
    result: undefined,
  },
  removeCollectionLoading: {
    loadingState: 'idle',
    progress: 0,
  },
};

export const appSlice = createSlice({
  name: 'app',
  initialState,
  reducers: {
    setCurrentResource: (state, action: PayloadAction<Resource>) => {
      state.currentResource = { ...action.payload };
      state.addCollectionResourcesLoading.loadingState = 'idle';
    },
    addToSelected: (state, action: PayloadAction<Resource>) => {
      const resource = action.payload;
      const { selectedResources } = state;
      if (
        !selectedResources.find(
          (selectedResource: Resource): boolean =>
            selectedResource.ref === resource.ref
        )
      ) {
        selectedResources.push({ ...resource });
      }
      state.selectedResources = [...selectedResources];
    },
    deleteFromSelected: (state, action: PayloadAction<Resource>) => {
      const resource: Resource = action.payload;
      const { selectedResources } = state;
      const existingIndex = selectedResources.findIndex(
        (currentResource: Resource): boolean =>
          currentResource.ref === resource.ref
      );
      if (existingIndex !== -1) {
        selectedResources.splice(existingIndex, 1);
        state.selectedResources = [...selectedResources];
      }
    },
    clearSelected: (state) => {
      state.selectedResources = [];
    },
    setLoggedOut: (state) => {
      const user: User = { loggedIn: false };
      state.user = user;
    },
    setUser: (state, action: PayloadAction<User>) => {
      const user: User = action.payload;
      state.user = { ...user };
    },
    setUserCollectionLoading: (state, action: PayloadAction<boolean>) => {
      state.userCollectionLoading = action.payload;
    },
    setUserCollections: (state, action: PayloadAction<Collection[]>) => {
      state.userCollections = action.payload.map((collection) => ({
        ...collection,
      }));
      state.userCollectionStale = false;
    },
    setUserCollectionStale: (state) => {
      state.userCollectionStale = true;
    },
    incrementCollectionResourcesLoading: (state) => {
      state.addCollectionResourcesLoading.progress =
        state.addCollectionResourcesLoading.progress + 1;
    },
    clearAddCollectionResourcesLoading: (state) => {
      state.addCollectionResourcesLoading.loadingState = 'idle';
    },
    clearCreateCollectionLoading: (state) => {
      state.createCollectionLoading.loadingState = 'idle';
      state.createCollectionLoading.result = undefined;
    },
    clearRemoveCollectionLoading: (state) => {
      state.removeCollectionLoading.loadingState = 'idle';
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getCsrfToken.fulfilled, (state, action) => {
      state.csrfToken = action.payload;
    });
    builder.addCase(removeCollection.pending, (state) => {
      state.removeCollectionLoading.loadingState = 'pending';
    });
    builder.addCase(removeCollection.rejected, (state) => {
      state.removeCollectionLoading.loadingState = 'failed';
    });
    builder.addCase(removeCollection.fulfilled, (state) => {
      state.removeCollectionLoading.loadingState = 'succeeded';
    });
    builder.addCase(createCollection.pending, (state) => {
      state.createCollectionLoading.loadingState = 'pending';
    });
    builder.addCase(createCollection.rejected, (state) => {
      state.createCollectionLoading.loadingState = 'failed';
    });
    builder.addCase(createCollection.fulfilled, (state, action) => {
      state.createCollectionLoading.loadingState = 'succeeded';
      const collectionId: number = action.payload;
      state.createCollectionLoading.result = collectionId;
    });
    builder.addCase(addSelectedToCollection.pending, (state) => {
      state.addCollectionResourcesLoading.loadingState = 'pending';
    });
    builder.addCase(addSelectedToCollection.rejected, (state) => {
      state.addCollectionResourcesLoading.loadingState = 'failed';
    });
    builder.addCase(addSelectedItemToCollection.pending, (state) => {
      state.addCollectionResourcesLoading.loadingState = 'pending';
    });
    builder.addCase(addSelectedItemToCollection.rejected, (state) => {
      state.addCollectionResourcesLoading.loadingState = 'failed';
    });
    builder.addCase(addSelectedItemToCollection.fulfilled, (state) => {
      state.addCollectionResourcesLoading.loadingState = 'succeeded';
    });
    builder.addCase(
      addSelectedToCollection.fulfilled,
      (
        state,
        action: PayloadAction<
          PromiseSettledResult<AddSelectedToCollectionCompleteParams>[]
        >
      ) => {
        state.addCollectionResourcesLoading.loadingState = 'succeeded';
        const results = action.payload;
        const selectedResources = state.selectedResources.map(
          (resource: Resource) => ({ ...resource } as Resource)
        );
        results.forEach((result) => {
          if (result.status === 'fulfilled') {
            const resourceIndex: number = selectedResources.findIndex(
              (resource) => resource.ref === result.value.resourceId
            );
            if (resourceIndex !== -1 && result.value.removeSelected) {
              selectedResources.splice(resourceIndex, 1);
            }
          } else {
            console.log(`Failed to add resource`);
          }
        });
        state.selectedResources = selectedResources.map((resource) => ({
          ...resource,
        }));
      }
    );
  },
});

// export const addSelectedToCollection: ActionCreator<
//   ThunkAction<Promise<Action>, RootState, void, Action<string>>
// > = async (collectionId: number) => {
//   return (
//     dispatch: ThunkDispatch<RootState, unknown, AnyAction>,
//     getState: () => RootState
//   ) => {
//     const state: RootState = getState();
//     Promise.all();
//   };
// };

export const appSelectors = {
  csrfToken: (state: RootState): string => state.app.csrfToken,
  currentResource: (state: RootState): Resource | null =>
    state.app.currentResource,
  selectedResources: (state: RootState): Resource[] =>
    state.app.selectedResources,
  isResourceSelected: (state: RootState, resourceId: number): boolean =>
    !!state.app.selectedResources.find(
      (resource: Resource): boolean => resource.ref === resourceId
    ),
  isLoggedIn: (state: RootState): boolean => state.app.user.loggedIn,
  username: (state: RootState): string =>
    (state.app.user.loggedIn && state.app.user.user?.fullname) || '',
  userCollections: (state: RootState): Collection[] =>
    state.app.userCollections,
  userCollectionsLoading: (state: RootState): boolean =>
    state.app.userCollectionLoading,
  userCollectionsStale: (state: RootState): boolean =>
    state.app.userCollectionStale,
  addCollectionResourcesLoadingState: (state: RootState): LoadingState =>
    state.app.addCollectionResourcesLoading.loadingState,
  createCollectionLoadingState: (state: RootState): AsyncRequestProgress =>
    state.app.createCollectionLoading,
};

export const {
  addToSelected,
  deleteFromSelected,
  setCurrentResource,
  setUser,
  setLoggedOut,
  setUserCollectionLoading,
  setUserCollections,
  clearSelected,
  clearAddCollectionResourcesLoading,
  clearCreateCollectionLoading,
} = appSlice.actions;
export default appSlice.reducer;
